import { Card, CardContent, CardHeader, Skeleton } from "@organizacaox/lai-design-system";
import { useSession } from "@/lib/session";
import { ConnectionsSkeleton } from "@/mcp-connections/connections-skeleton";

export function SettingsSkeleton() {
  const { session, hasCapability } = useSession();
  const showConnections = !!session?.identity.tenantId && hasCapability("mcp.connections.read");
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="w-full min-w-0 motion-reduce:[&_[data-slot=skeleton]]:animate-none"
    >
      <span className="sr-only">Carregando configurações…</span>
      <div aria-hidden="true" className="flex min-w-0 flex-col gap-5">
        <div className="space-y-3 py-1">
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-56 max-w-[80%]" />
            <Skeleton variant="circle" className="size-5 shrink-0" />
          </div>
          <Skeleton className="h-4 w-full max-w-lg" />
          <Skeleton className="h-4 w-3/5 sm:hidden" />
        </div>
        <div className="grid min-w-0 gap-5 xl:grid-cols-2">
          <Card>
            <CardHeader className="min-w-0 grid-cols-1 gap-2">
              <Skeleton className="h-5 w-28" />
              <Skeleton className="h-4 w-full max-w-72" />
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Skeleton className="h-3.5 w-24" />
                <div className="grid w-full max-w-sm grid-cols-3 gap-px rounded-lg border p-1">
                  {[0, 1, 2].map((index) => (
                    <div key={index} className="flex h-8 items-center justify-center gap-1.5">
                      <Skeleton variant="circle" className="hidden size-3.5 min-[360px]:block" />
                      <Skeleton className="h-3 w-10 max-w-full" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Skeleton className="h-3.5 w-32" />
                <div className="flex min-h-10 w-full max-w-sm items-center justify-between gap-2 rounded-lg border p-2">
                  <Skeleton className="h-3 w-24 max-w-[45%]" />
                  <div className="flex items-center gap-2">
                    <Skeleton className="size-5 rounded-md" />
                    <Skeleton className="h-3 w-7" />
                    <Skeleton className="size-5 rounded-md" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="min-w-0 grid-cols-1 gap-2">
              <Skeleton className="h-5 w-52 max-w-full" />
              <Skeleton className="h-4 w-full max-w-80" />
            </CardHeader>
            <CardContent className="flex flex-col gap-6">
              <div className="space-y-2">
                <Skeleton className="h-3.5 w-14" />
                <div className="flex h-9 items-center justify-between gap-3 rounded-md border px-3">
                  <Skeleton className="h-3.5 w-32 max-w-[80%]" />
                  <Skeleton className="size-3 shrink-0" />
                </div>
                <Skeleton className="h-4 w-full max-w-80" />
              </div>
              <div className="space-y-3">
                <Skeleton className="h-3.5 w-28" />
                {["w-4/5", "w-3/5"].map((width) => (
                  <div key={width} className="flex items-start gap-3 rounded-xl border p-4">
                    <Skeleton variant="circle" className="mt-0.5 size-4 shrink-0" />
                    <div className="min-w-0 flex-1 space-y-2">
                      <Skeleton className="h-3.5 w-24 max-w-full" />
                      <Skeleton className={`h-4 ${width}`} />
                      <Skeleton className="h-4 w-2/5 sm:hidden" />
                    </div>
                  </div>
                ))}
              </div>
              <Skeleton className="h-3 w-full max-w-80" />
            </CardContent>
          </Card>
        </div>
        {showConnections ? (
          <Card>
            <CardHeader className="min-w-0 grid-cols-1 gap-2">
              <Skeleton className="h-5 w-52 max-w-full" />
              <Skeleton className="h-4 w-full max-w-2xl" />
              <Skeleton className="h-4 w-3/5 max-w-lg" />
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
              <Skeleton className="h-4 w-full max-w-3xl" />
              <ConnectionsSkeleton />
            </CardContent>
          </Card>
        ) : null}
        <Card>
          <CardContent className="grid items-center gap-5 sm:grid-cols-[auto_1fr] lg:grid-cols-[auto_1fr_auto]">
            <div className="w-fit rounded-2xl border p-3">
              <Skeleton className="size-16 rounded-xl" />
            </div>
            <div className="min-w-0 space-y-2">
              <Skeleton className="h-6 w-48 max-w-full" />
              <Skeleton className="h-4 w-full max-w-xl" />
              <Skeleton className="h-4 w-4/5 max-w-lg" />
            </div>
            <Skeleton className="h-9 w-full rounded-md sm:col-span-2 lg:col-span-1 lg:w-44" />
          </CardContent>
        </Card>
        {session?.profile ? (
          <Card>
            <CardHeader className="min-w-0 grid-cols-1 gap-2">
              <Skeleton className="h-5 w-12" />
              <Skeleton className="h-4 w-full max-w-sm" />
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex w-full max-w-sm flex-col gap-1.5">
                <Skeleton className="h-3.5 w-20" />
                <Skeleton className="h-9 w-full rounded-md" />
              </div>
              <Skeleton className="h-9 w-28 rounded-md" />
            </CardContent>
          </Card>
        ) : null}
        <Card>
          <CardHeader className="min-w-0 grid-cols-1 gap-2">
            <Skeleton className="h-5 w-16" />
            <Skeleton className="h-4 w-full max-w-72" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-9 w-20 rounded-md" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
